import axios from "axios";
import { useAuthStore } from "@/lib/auth/store";
import { ApiBusinessError } from "@/lib/api/errors";

export interface ApiError {
  field?: string;
  message: string;
}

export interface ApiResponse<T> {
  success: boolean;
  code: string;
  data: T;
  timestamp: string;
  errors: ApiError[];
  requestId: string;
}

export const http = axios.create({
  baseURL: "/api/v1",
});

http.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// The backend issues a single 24h access token — no refresh flow. A 401 means
// the session is gone: clear the store and send the user back to /login.
http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Unwraps the ApiResponse envelope. A `success: false` body is always HTTP
 * 200 (business/validation failures never use HTTP error status here), so
 * this throws an ApiBusinessError explicitly rather than letting callers
 * mistake `data: null` for a valid empty result.
 */
export async function unwrap<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  const response = await promise;
  const body = response.data;
  if (!body.success) {
    throw new ApiBusinessError(body.code, `Request failed: ${body.code}`, body.errors);
  }
  return body.data;
}
